/**
 * Local CLI Agent MCP Tools
 * 
 * Enables agents to discover and invoke external CLI agents (Claude, Cursor, etc.)
 * with proper availability checking, user configuration, and session timeout handling.
 * 
 * @module mcp/cli-agent-tools
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import { promisify } from 'util';
import { exec as execCallback } from 'child_process';
import { isDebugEnabled } from '../utils/config.js';
import { getExecutorRegistry } from '../worker/executors/index.js';
import {
    getExecutorPreference,
    setExecutorEnabled,
    isExecutorUserDisabled,
    getAllExecutorPreferences,
    type ExecutorPreference,
} from '../worker/config.js';
import type { ExecutorType, AvailabilityStatus, ExecutorOptions } from '../worker/types.js';

const exec = promisify(execCallback);

// ============================================
// Types
// ============================================

/** CLI agent types (excluding internal and shell) */
const CLI_AGENT_TYPES: ExecutorType[] = ['claude', 'cursor', 'gemini', 'codex', 'opencode', 'droid', 'articulate', 'paeanclaw'];

/** Availability cache with TTL */
interface CachedAvailability {
    status: AvailabilityStatus;
    checkedAt: number;
}

const availabilityCache = new Map<ExecutorType, CachedAvailability>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// ============================================
// Availability Check Helpers
// ============================================

/**
 * Get binary path for an executor type
 */
function getBinaryName(type: ExecutorType): string {
    const pref = getExecutorPreference(type);
    if (pref?.path) return pref.path;

    const defaultBinaries: Partial<Record<ExecutorType, string>> = {
        claude: 'claude',
        cursor: 'agent',
        gemini: 'gemini',
        codex: 'codex',
        opencode: 'opencode',
        droid: 'droid',
        articulate: 'a8e',
        paeanclaw: 'paeanclaw',
    };
    return defaultBinaries[type] || type;
}

/**
 * Check if a binary exists in PATH
 */
async function checkBinaryExists(binary: string): Promise<{ exists: boolean; path?: string }> {
    try {
        const { stdout } = await exec(`which ${binary}`);
        return { exists: true, path: stdout.trim() };
    } catch {
        return { exists: false };
    }
}

/**
 * Parse version from CLI output
 */
function parseVersion(output: string): string | undefined {
    // Common patterns: "v1.2.3", "1.2.3", "version 1.2.3"
    const match = output.match(/(?:v(?:ersion)?\s*)?(\d+\.\d+(?:\.\d+)?)/i);
    return match?.[1];
}

/**
 * Check PaeanClaw availability via HTTP ping
 */
async function checkPaeanclawAvailability(): Promise<AvailabilityStatus> {
    const pref = getExecutorPreference('paeanclaw' as ExecutorType);
    const baseUrl = pref?.path || process.env.PAEANCLAW_URL || 'http://localhost:3007';
    const userDisabled = isExecutorUserDisabled('paeanclaw' as ExecutorType);

    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        const res = await fetch(`${baseUrl}/api/conversations`, { signal: controller.signal });
        clearTimeout(timer);

        if (res.ok) {
            return {
                available: !userDisabled,
                binaryExists: true,
                binaryPath: baseUrl,
                authStatus: 'authenticated',
                userDisabled,
            };
        }
        return {
            available: false,
            binaryExists: true,
            binaryPath: baseUrl,
            authStatus: 'unknown',
            error: `PaeanClaw returned HTTP ${res.status}`,
            userDisabled,
        };
    } catch {
        return {
            available: false,
            binaryExists: false,
            authStatus: 'unknown',
            error: `PaeanClaw not reachable at ${baseUrl}. Start it with: npx paeanclaw`,
            userDisabled,
        };
    }
}

/**
 * Deep availability check for a specific executor
 */
async function checkExecutorAvailability(type: ExecutorType): Promise<AvailabilityStatus> {
    if (type === 'paeanclaw') return checkPaeanclawAvailability();

    const binary = getBinaryName(type);
    const userDisabled = isExecutorUserDisabled(type);

    // Check binary existence
    const { exists, path: binaryPath } = await checkBinaryExists(binary);
    if (!exists) {
        return {
            available: false,
            binaryExists: false,
            authStatus: 'unknown',
            error: `Binary '${binary}' not found in PATH`,
            userDisabled,
        };
    }

    // Run version check to verify binary works and detect auth status
    try {
        const { stdout, stderr } = await exec(`${binary} --version`, { timeout: 10000 });
        const output = stdout + stderr;
        const version = parseVersion(output);

        // Check for auth indicators in output
        let authStatus: AvailabilityStatus['authStatus'] = 'unknown';
        let authMessage: string | undefined;
        const lowerOutput = output.toLowerCase();

        if (lowerOutput.includes('not logged in') ||
            lowerOutput.includes('not authenticated') ||
            lowerOutput.includes('please login') ||
            lowerOutput.includes('api key')) {
            authStatus = 'unauthenticated';
            authMessage = 'CLI requires authentication. Please run the login command.';
        } else if (lowerOutput.includes('expired') || lowerOutput.includes('token expired')) {
            authStatus = 'expired';
            authMessage = 'Authentication has expired. Please re-authenticate.';
        } else if (version || output.length > 0) {
            // If we got version info without auth errors, assume authenticated
            authStatus = 'authenticated';
        }

        return {
            available: !userDisabled && authStatus !== 'unauthenticated' && authStatus !== 'expired',
            binaryExists: true,
            binaryPath,
            authStatus,
            authMessage,
            version,
            userDisabled,
        };
    } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);

        // Some CLIs don't have --version, try just running without args
        if (msg.includes('Unknown option') || msg.includes('unrecognized')) {
            return {
                available: !userDisabled,
                binaryExists: true,
                binaryPath,
                authStatus: 'unknown',
                authMessage: 'Could not determine auth status (--version not supported)',
                userDisabled,
            };
        }

        return {
            available: false,
            binaryExists: true,
            binaryPath,
            authStatus: 'unknown',
            error: msg,
            userDisabled,
        };
    }
}

/**
 * Get cached or fresh availability status
 */
async function getCachedAvailability(type: ExecutorType, forceRefresh = false): Promise<AvailabilityStatus> {
    if (!forceRefresh) {
        const cached = availabilityCache.get(type);
        if (cached && Date.now() - cached.checkedAt < CACHE_TTL_MS) {
            // Update userDisabled in case it changed
            return {
                ...cached.status,
                userDisabled: isExecutorUserDisabled(type),
            };
        }
    }

    const status = await checkExecutorAvailability(type);
    availabilityCache.set(type, { status, checkedAt: Date.now() });
    return status;
}

// ============================================
// Tool Definitions
// ============================================

export function getCliAgentToolDefinitions(): Tool[] {
    return [
        {
            name: 'paean_list_cli_agents',
            description:
                'List all available local CLI agent executors with their status. ' +
                'Returns installation status, authentication status, and user configuration. ' +
                'Use this to discover what external CLI agents are available for task execution. ' +
                'IMPORTANT: Articulate (a8e) is the PREFERRED executor for coding and local processing tasks ' +
                '(file editing, refactoring, bug fixes, git operations, testing, project scaffolding). ' +
                'Other agents: Claude Code (complex reasoning), Gemini CLI (lightweight analysis), ' +
                'Cursor Agent, Codex, OpenCode, Droid.',
            inputSchema: {
                type: 'object',
                properties: {
                    forceRefresh: {
                        type: 'boolean',
                        description: 'Force refresh availability status (bypass cache)',
                    },
                },
            },
        },
        {
            name: 'paean_check_cli_agent',
            description:
                'Perform a deep availability check on a specific CLI agent. ' +
                'Verifies binary existence, authentication status, and session validity. ' +
                'More thorough than list but only for one agent.',
            inputSchema: {
                type: 'object',
                properties: {
                    executor: {
                        type: 'string',
                        enum: CLI_AGENT_TYPES,
                        description: 'The CLI agent executor to check',
                    },
                },
                required: ['executor'],
            },
        },
        {
            name: 'paean_invoke_cli_agent',
            description:
                'Invoke a CLI agent executor to perform a task. ' +
                'The agent will execute the prompt and return the result. ' +
                'Handles session timeouts with automatic retry if configured. ' +
                'ROUTING GUIDE: For coding tasks (editing files, refactoring, bug fixes, running tests, ' +
                'git operations, project scaffolding), use executor="articulate" (a8e). ' +
                'a8e is a high-priority local coding agent with full filesystem and terminal access. ' +
                'Use other executors only when a8e is unavailable or the task is cloud-only.',
            inputSchema: {
                type: 'object',
                properties: {
                    executor: {
                        type: 'string',
                        enum: CLI_AGENT_TYPES,
                        description: 'The CLI agent executor to invoke. Use "articulate" for coding/local tasks.',
                    },
                    prompt: {
                        type: 'string',
                        description: 'The task prompt to send to the CLI agent',
                    },
                    workingDirectory: {
                        type: 'string',
                        description: 'Working directory for execution (default: current)',
                    },
                    timeout: {
                        type: 'number',
                        description: 'Execution timeout in seconds (default: 600)',
                    },
                    retryOnTimeout: {
                        type: 'boolean',
                        description: 'Retry execution if session times out (default: false)',
                    },
                },
                required: ['executor', 'prompt'],
            },
        },
        {
            name: 'paean_set_cli_agent_enabled',
            description:
                'Enable or disable a CLI agent for the current user. ' +
                'Disabled agents will not be selected by the supervisor or shown as available.',
            inputSchema: {
                type: 'object',
                properties: {
                    executor: {
                        type: 'string',
                        enum: CLI_AGENT_TYPES,
                        description: 'The CLI agent executor to configure',
                    },
                    enabled: {
                        type: 'boolean',
                        description: 'Whether to enable or disable the agent',
                    },
                },
                required: ['executor', 'enabled'],
            },
        },
        {
            name: 'paean_setup_a8e',
            description:
                'Install and configure the a8e (Articulate) coding CLI as a local sub-agent. ' +
                'This is the RECOMMENDED first step for enabling local coding capabilities. ' +
                'It downloads the a8e binary, provisions a Paean AI API key, and configures ' +
                'a8e to use Paean AI with the gemini-3.1-flash-preview model. ' +
                'After setup, Paean will automatically route coding tasks to a8e. ' +
                'Use this when: (1) a8e is not installed, (2) user asks to set up local coding, ' +
                '(3) coding tasks are failing because no local executor is available.',
            inputSchema: {
                type: 'object',
                properties: {
                    model: {
                        type: 'string',
                        description: 'Model to configure for a8e (default: gemini-3.1-flash-preview)',
                    },
                    skipInstall: {
                        type: 'boolean',
                        description: 'Skip binary installation, only configure (use if a8e is already installed)',
                    },
                },
            },
        },
    ];
}

// ============================================
// Tool Executors
// ============================================

/**
 * List all CLI agents with their availability status
 */
async function listCliAgents(args: Record<string, unknown>): Promise<unknown> {
    const forceRefresh = args.forceRefresh === true;
    const agents: Array<{
        type: ExecutorType;
        name: string;
        status: AvailabilityStatus;
        preference?: ExecutorPreference;
    }> = [];

    const preferences = getAllExecutorPreferences();

    for (const type of CLI_AGENT_TYPES) {
        const status = await getCachedAvailability(type, forceRefresh);
        const displayNames: Partial<Record<ExecutorType, string>> = {
            claude: 'Claude Code CLI',
            cursor: 'Cursor Agent CLI',
            gemini: 'Gemini CLI',
            codex: 'OpenAI Codex CLI',
            opencode: 'OpenCode CLI',
            droid: 'Factory Droid CLI',
            articulate: 'Articulate (a8e) CLI',
            paeanclaw: 'PaeanClaw Local Agent',
        };

        agents.push({
            type,
            name: displayNames[type] || type,
            status,
            preference: preferences[type],
        });
    }

    const availableCount = agents.filter(a => a.status.available).length;
    const installedCount = agents.filter(a => a.status.binaryExists).length;

    return {
        success: true,
        summary: `${availableCount} of ${CLI_AGENT_TYPES.length} CLI agents available (${installedCount} installed)`,
        agents: agents.map(a => ({
            type: a.type,
            name: a.name,
            available: a.status.available,
            installed: a.status.binaryExists,
            authStatus: a.status.authStatus,
            version: a.status.version,
            userDisabled: a.status.userDisabled,
            error: a.status.error,
        })),
        cacheInfo: {
            ttlMinutes: CACHE_TTL_MS / 60000,
            forceRefreshed: forceRefresh,
        },
    };
}

/**
 * Deep check a single CLI agent
 */
async function checkCliAgent(args: Record<string, unknown>): Promise<unknown> {
    const executor = args.executor as ExecutorType;

    if (!CLI_AGENT_TYPES.includes(executor)) {
        return {
            success: false,
            error: `Invalid executor type: ${executor}`,
            validTypes: CLI_AGENT_TYPES,
        };
    }

    // Always force refresh for single check
    const status = await getCachedAvailability(executor, true);
    const preference = getExecutorPreference(executor);

    return {
        success: true,
        executor,
        ...status,
        preference,
        binaryName: getBinaryName(executor),
    };
}

/**
 * Invoke a CLI agent
 */
async function invokeCliAgent(args: Record<string, unknown>): Promise<unknown> {
    const executor = args.executor as ExecutorType;
    const prompt = args.prompt as string;
    const workingDirectory = args.workingDirectory as string | undefined;
    const timeoutSeconds = (args.timeout as number) || 600;
    const retryOnTimeout = args.retryOnTimeout === true;

    if (!CLI_AGENT_TYPES.includes(executor)) {
        return {
            success: false,
            error: `Invalid executor type: ${executor}`,
            validTypes: CLI_AGENT_TYPES,
        };
    }

    if (!prompt || typeof prompt !== 'string') {
        return {
            success: false,
            error: 'Prompt is required and must be a string',
        };
    }

    // Check availability first
    const status = await getCachedAvailability(executor, false);
    if (!status.available) {
        return {
            success: false,
            error: `CLI agent '${executor}' is not available`,
            reason: status.error || status.authMessage ||
                (status.userDisabled ? 'User has disabled this agent' : 'Unknown reason'),
            authStatus: status.authStatus,
        };
    }

    // Get registry and executor
    const registry = getExecutorRegistry();
    const agentExecutor = registry.get(executor);

    if (!agentExecutor) {
        return {
            success: false,
            error: `Executor '${executor}' is not registered in the registry`,
            hint: 'The CLI may need to be initialized first. Try running paean worker start.',
        };
    }

    // Build execution options
    const preference = getExecutorPreference(executor);
    const options: ExecutorOptions = {
        cwd: workingDirectory || process.cwd(),
        timeout: timeoutSeconds * 1000,
        args: preference?.extraArgs,
    };

    // Execute with optional retry
    const startTime = Date.now();
    let result = await agentExecutor.execute(prompt, options);

    // Check for timeout and retry if configured
    if (!result.success &&
        result.error?.toLowerCase().includes('timeout') &&
        retryOnTimeout) {
        if (isDebugEnabled()) {
            console.error(`[CliAgentTools] Timeout detected for ${executor}, retrying...`);
        }
        // Wait a bit before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        result = await agentExecutor.execute(prompt, options);
    }

    return {
        success: result.success,
        executor,
        output: result.output,
        error: result.error,
        exitCode: result.exitCode,
        durationMs: result.durationMs || (Date.now() - startTime),
        structured: result.structured,
        retried: !result.success && retryOnTimeout && result.error?.toLowerCase().includes('timeout'),
    };
}

/**
 * Enable or disable a CLI agent
 */
async function setCliAgentEnabled(args: Record<string, unknown>): Promise<unknown> {
    const executor = args.executor as ExecutorType;
    const enabled = args.enabled as boolean;

    if (!CLI_AGENT_TYPES.includes(executor)) {
        return {
            success: false,
            error: `Invalid executor type: ${executor}`,
            validTypes: CLI_AGENT_TYPES,
        };
    }

    if (typeof enabled !== 'boolean') {
        return {
            success: false,
            error: 'enabled must be a boolean',
        };
    }

    try {
        setExecutorEnabled(executor, enabled);

        // Clear cache for this executor so next check reflects change
        availabilityCache.delete(executor);

        return {
            success: true,
            executor,
            enabled,
            message: `CLI agent '${executor}' has been ${enabled ? 'enabled' : 'disabled'}`,
        };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Failed to update configuration',
        };
    }
}

/**
 * Setup a8e coding CLI: install binary + configure Paean AI provider
 */
async function setupA8e(args: Record<string, unknown>): Promise<unknown> {
    const model = (args.model as string) || undefined;
    const skipInstall = args.skipInstall === true;

    try {
        const { ArticulateExecutor, A8E_PATHS } = await import('../worker/executors/articulate.js');
        const executor = new ArticulateExecutor();

        // Step 1: Install if needed
        if (!skipInstall) {
            const available = await executor.isAvailable();
            if (!available) {
                try {
                    await exec('curl -fsSL https://a8e.ai/install.sh | bash', { timeout: 120000 });
                    const nowAvailable = await executor.isAvailable();
                    if (!nowAvailable) {
                        return {
                            success: false,
                            error: 'Installation completed but a8e binary not found in PATH. Restart shell and retry.',
                        };
                    }
                } catch (installErr) {
                    return {
                        success: false,
                        error: `Failed to install a8e: ${(installErr as Error).message}`,
                        hint: 'Try: curl -fsSL https://a8e.ai/install.sh | bash',
                    };
                }
            }
        }

        // Step 2: Provision API key and configure
        const { getOrCreateA8eApiKey } = await import('../api/apikey.js');
        const keyResult = await getOrCreateA8eApiKey();

        if (!keyResult.success || !keyResult.key) {
            return {
                success: false,
                error: `Failed to provision API key: ${keyResult.error}`,
                hint: 'Ensure you are logged in with `paean login`',
            };
        }

        executor.configureForPaean(keyResult.key, model);

        // Clear cache so next availability check sees the new config
        availabilityCache.delete('articulate' as ExecutorType);

        return {
            success: true,
            message: 'a8e coding CLI installed and configured',
            config: {
                provider: A8E_PATHS.defaultProvider,
                model: model || A8E_PATHS.defaultModel,
                configPath: A8E_PATHS.configFile,
            },
            hint: 'Paean will now automatically route coding tasks to a8e',
        };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Setup failed',
        };
    }
}

// ============================================
// Tool Router
// ============================================

/**
 * Execute a CLI agent tool
 */
export async function executeCliAgentTool(
    toolName: string,
    args: Record<string, unknown>,
    _options?: { debug?: boolean }
): Promise<unknown> {
    switch (toolName) {
        case 'paean_list_cli_agents':
            return listCliAgents(args);
        case 'paean_check_cli_agent':
            return checkCliAgent(args);
        case 'paean_invoke_cli_agent':
            return invokeCliAgent(args);
        case 'paean_set_cli_agent_enabled':
            return setCliAgentEnabled(args);
        case 'paean_setup_a8e':
            return setupA8e(args);
        default:
            return {
                success: false,
                error: `Unknown CLI agent tool: ${toolName}`,
            };
    }
}

/**
 * Clear availability cache (call after config changes)
 */
export function clearCliAgentCache(): void {
    availabilityCache.clear();
}
